import React, { ReactNode } from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import Typography from '../../text/Typography';
import { NavItemType } from '../navTypes';

const Wrapper = styled.div`
  margin-top: ${spacing[5]}px;
  ${breakpoints.tablet} {
    margin-top: ${spacing[7]}px;
  }
  ${breakpoints.xl} {
    margin-top: ${spacing[9]}px;
  }
`;

const IconsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${spacing[2]}px 0;

  ${breakpoints.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${spacing[2]}px 0;
  ${breakpoints.tablet} {
    align-items: center;
    padding: 0;
  }
  a {
    margin-right: ${spacing[4]}px;
    :last-child {
      margin-right: 0;
    }
  }
  & > div {
    margin-right: ${spacing[4]}px;
    :last-child {
      margin-right: 0;
    }
  }
  img {
    height: 24px;
    width: 24px;
    object-fit: contain;
  }
`;

const LegalInfoWrapper = styled.div`
  padding: ${spacing[2]}px 0 ${spacing[6]}px;
  border-top: 1px solid ${colors.darkGrey};
  border-bottom-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  ${breakpoints.tablet} {
    flex-direction: row;
    justify-content: space-between;
  }
`;
const PolicyLinks = styled.div`
  max-width: 470px;
  min-width: 280px;
  margin: ${spacing[1]}px 0;
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${breakpoints.tablet} {
    width: 70%;
    flex-direction: row;
  }
`;

const SecondaryLinkText = styled.a`
  display: flex;
  text-decoration: none;
  cursor: pointer;
`;

const ImageLink = styled.a`
  display: flex;
  text-decoration: none;
  align-items: center;
  color: ${colors.blue};
  cursor: pointer;
  img {
    height: 16px;
    object-fit: contain;
    margin-right: ${spacing[1]}px;
  }
`;

const Disclaimer = styled(Typography)`
  max-width: 570px;
`;
const Copyright = styled(Typography)`
  margin-top: ${spacing[1]}px;
`;

type Props = {
  socialMedia?: NavItemType[];
  paymentProviders?: ReactNode[];
  policyLinks?: NavItemType[];
  disclaimer?: string;
  copyright?: string;
};

const SocialMediaInfo = ({
  socialMedia,
  paymentProviders,
  policyLinks,
  disclaimer,
  copyright,
}: Props) => {
  return (
    <Wrapper>
      <IconsWrapper>
        {!!socialMedia?.length && (
          <IconsContainer>
            {socialMedia.map(({ title, image, href }: NavItemType, index) => {
              return (
                <Link href={href as string} passHref={!!href} key={index}>
                  <a aria-label={title}>{image}</a>
                </Link>
              );
            })}
          </IconsContainer>
        )}
        {!!paymentProviders?.length && <IconsContainer>{paymentProviders}</IconsContainer>}
      </IconsWrapper>
      <LegalInfoWrapper>
        <div>
          {!!policyLinks?.length && (
            <PolicyLinks>
              {policyLinks.map(({ title, content, image, href }, index) => {
                if (image) {
                  return (
                    <Link href={href as string} passHref={!!href} key={index}>
                      <ImageLink aria-label={title}>
                        {image}
                        <Typography variant="smallText" color={colors.blue}>
                          {content}
                        </Typography>
                      </ImageLink>
                    </Link>
                  );
                }
                return (
                  <Link href={href as string} passHref={!!href} key={index}>
                    <SecondaryLinkText aria-label={title}>
                      <Typography variant="smallText" color={colors.blue}>
                        {content}
                      </Typography>
                    </SecondaryLinkText>
                  </Link>
                );
              })}
            </PolicyLinks>
          )}
          {disclaimer && (
            <Disclaimer variant="smallText" color={colors.darkGray}>
              {disclaimer}
            </Disclaimer>
          )}
        </div>
        {copyright && (
          <Copyright variant="smallText" color={colors.darkGray}>
            {copyright}
          </Copyright>
        )}
      </LegalInfoWrapper>
    </Wrapper>
  );
};

export default SocialMediaInfo;
